import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../components/ui/Button';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';

const InsightsPreview = () => {
  const insights = [
    {
      id: 1,
      title: "The Future of Digital Transformation: AI-Driven Business Evolution",
      excerpt: "Explore how artificial intelligence is reshaping business operations and creating new opportunities for competitive advantage in 2024 and beyond.",
      category: "Digital Innovation",
      readTime: "8 min read",
      publishDate: "July 8, 2024",
      author: "Sarah Mitchell",
      authorRole: "Chief Strategy Officer",
      image: "https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=600&h=400&fit=crop",
      featured: true
    },
    {
      id: 2,
      title: "Operational Excellence in Manufacturing: A Data-Driven Approach",
      excerpt: "Discover proven methodologies for achieving operational excellence through data analytics and process optimization.",
      category: "Operations",
      readTime: "6 min read",
      publishDate: "July 5, 2024",
      author: "Michael Chen",
      authorRole: "Operations Director",
      image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=600&h=400&fit=crop",
      featured: false
    },
    {
      id: 3,
      title: "Strategic Planning in Uncertain Times: Building Resilient Organizations",
      excerpt: "Learn how to develop robust strategic plans that adapt to market volatility and drive sustainable growth.",
      category: "Strategy",
      readTime: "10 min read",
      publishDate: "July 3, 2024",
      author: "David Rodriguez",
      authorRole: "Senior Consultant",
      image: "https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&h=400&fit=crop",
      featured: false
    }
  ];

  const getCategoryColor = (category) => {
    const colors = {
      'Digital Innovation': 'bg-brand-cyan/20 text-brand-cyan border border-brand-cyan/30',
      'Operations': 'bg-brand-green/20 text-brand-green border border-brand-green/30',
      'Strategy': 'bg-brand-purple/20 text-brand-purple border border-brand-purple/30'
    };
    return colors[category] || 'bg-white/20 text-white border border-white/30';
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-foreground font-heading mb-4">
            Latest Insights
          </h2>
          <p className="text-xl text-text-secondary font-body max-w-3xl mx-auto">
            Stay ahead with our thought leadership on telecommunications engineering, infrastructure solutions, and industry innovations
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Featured Article */}
          <div className="lg:col-span-2">
            <article className="bg-white rounded-2xl shadow-brand-lg overflow-hidden hover-lift">
              <div className="relative h-64 overflow-hidden">
                <Image
                  src={insights[0].image}
                  alt={insights[0].title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute top-4 left-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getCategoryColor(insights[0].category)}`}>
                    {insights[0].category}
                  </span>
                </div>
                <div className="absolute top-4 right-4 bg-brand-orange text-white px-3 py-1 rounded-full text-xs font-medium">
                  Featured
                </div>
              </div>
              
              <div className="p-8">
                <div className="flex items-center text-sm text-text-secondary mb-4">
                  <Icon name="Calendar" size={16} className="mr-2" />
                  <span>{insights[0].publishDate}</span>
                  <span className="mx-2">•</span>
                  <Icon name="Clock" size={16} className="mr-2" />
                  <span>{insights[0].readTime}</span>
                </div>
                
                <h3 className="text-2xl font-bold text-primary font-heading mb-4 leading-tight">
                  {insights[0].title}
                </h3>
                
                <p className="text-text-secondary font-body mb-6 leading-relaxed">
                  {insights[0].excerpt}
                </p>
                
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mr-3">
                      <span className="text-white font-semibold text-sm">
                        {insights[0].author.split(' ').map(n => n[0]).join('')}
                      </span>
                    </div>
                    <div>
                      <p className="font-semibold text-primary text-sm">{insights[0].author}</p>
                      <p className="text-text-secondary text-xs">{insights[0].authorRole}</p>
                    </div>
                  </div>
                  
                  <Link to="/insights-blog">
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="ArrowRight"
                      iconPosition="right"
                      className="text-black"
                    >
                      Read More
                    </Button>
                  </Link>
                </div>
              </div>
            </article>
          </div>

          {/* Side Articles */}
          <div className="space-y-6">
            {insights.slice(1).map((insight) => (
              <article key={insight.id} className="bg-white rounded-xl shadow-brand border border-border overflow-hidden hover-lift">
                <div className="relative h-32 overflow-hidden">
                  <Image
                    src={insight.image}
                    alt={insight.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-2 left-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(insight.category)}`}>
                      {insight.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex items-center text-xs text-text-secondary mb-3">
                    <Icon name="Calendar" size={14} className="mr-1" />
                    <span>{insight.publishDate}</span>
                    <span className="mx-2">•</span>
                    <Icon name="Clock" size={14} className="mr-1" />
                    <span>{insight.readTime}</span>
                  </div>
                  
                  <h4 className="text-lg font-bold text-primary font-heading mb-3 leading-tight">
                    {insight.title}
                  </h4>
                  
                  <p className="text-text-secondary font-body text-sm mb-4 leading-relaxed">
                    {insight.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center mr-2">
                        <span className="text-white font-semibold text-xs">
                          {insight.author.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold text-primary text-xs">{insight.author}</p>
                        <p className="text-text-secondary text-xs">{insight.authorRole}</p>
                      </div>
                    </div>
                    
                    <Link to="/insights-blog">
                      <Button 
                        variant="ghost" 
                        size="xs"
                        iconName="ArrowRight"
                        iconPosition="right"
                      >
                        Read
                      </Button>
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-surface rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-slate-900 font-heading mb-4">
              Stay Informed with Our Newsletter
            </h3>
            <p className="text-text-secondary font-body mb-6 max-w-2xl mx-auto">
              Get weekly insights on industry trends, best practices, and innovative solutions delivered directly to your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 rounded-lg border border-border focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <Button 
                variant="default"
                className="bg-brand-orange hover:bg-brand-orange/90"
                iconName="Send"
                iconPosition="right"
              >
                Subscribe
              </Button>
            </div>
          </div>
        </div>

        {/* View All CTA */}
        <div className="mt-12 text-center">
          <Link to="/insights-blog">
            <Button 
              variant="outline" 
              size="lg"
              iconName="BookOpen"
              iconPosition="left"
            >
              View All Insights
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default InsightsPreview;